"use client"

import { useState, useEffect, useMemo } from "react"
import {
  getAMCReport,
  getExpiredAMCReport,
  getInactiveAMCReport,
  markSubscriptionInactive,
  markSubscriptionActive
} from "../../../services/AdminService"
import "./subscriptions.css"
import * as XLSX from "xlsx"


const groupExpiringByClient = (data) => {
  const map = {}

  data
    .filter((item) => item.isExpiringThisMonth)
    .forEach((item) => {
      if (!map[item.UserID]) {
        map[item.UserID] = {
          UserID: item.UserID,
          SubscriberName: item.SubscriberName,
          ContactNumber: item.ContactNumber,
          devices: [],
          nearestExpiry: item.planEnd,
        }
      }

      map[item.UserID].devices.push(item.deviceID)

      if (new Date(item.planEnd) < new Date(map[item.UserID].nearestExpiry)) {
        map[item.UserID].nearestExpiry = item.planEnd
      }
    })

  return Object.values(map)
}

const SubscriptionManagement = () => {
  // State Management
  const [allData, setAllData] = useState([])
  // const [filteredData, setFilteredData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [clientId, setClientId] = useState(null)
  // Filter States
  const [filters, setFilters] = useState({
    status: "all",
    searchTerm: "",
    dateRange: "all",
    customStartDate: "",
    customEndDate: "",
    product: "all",
    contractType: "all",
  })
  const [expandedClients, setExpandedClients] = useState({})
  const toggleClient = (userId) => {

    setExpandedClients((prev) => ({
      ...prev,
      [userId]: !prev[userId]
    }))

  }
  const getUniqueClientCount = (data, filterFn) => {
    const clients = data
      .filter(filterFn)
      .map((item) => item.UserID)
      .filter(Boolean)

    return new Set(clients).size
  }
  // View States
  const [currentPage, setCurrentPage] = useState(1)
  useEffect(() => {
    setCurrentPage(1)
  }, [filters])
  const [itemsPerPage] = useState(10)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" })

  // Notification States
  const [expiringThisMonth, setExpiringThisMonth] = useState([])
  const [showNotifications, setShowNotifications] = useState(true)

  // Toast States
  const [toasts, setToasts] = useState([])
  // Toast Functions
  const showToast = (message, type = "info", duration = 5000) => {
    const id = Date.now() + Math.random()
    const newToast = {
      id,
      message,
      type,
      duration,
      timestamp: new Date().toLocaleTimeString(),
    }

    setToasts((prev) => [...prev, newToast])

    setTimeout(() => {
      removeToast(id)
    }, duration)
  }

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }

  // Toast Component
  const ToastContainer = () => (
    <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 1055 }}>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast show align-items-center text-bg-${toast.type === "error" ? "danger" : toast.type} border-0`}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div className="d-flex">
            <div className="toast-body d-flex align-items-center">
              <i className={`fas fa-${getToastIcon(toast.type)} me-2`}></i>
              <div className="flex-grow-1">
                <div className="fw-semibold">{getToastTitle(toast.type)}</div>
                <div className="small">{toast.message}</div>
                <div className="text-muted small">{toast.timestamp}</div>
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white me-2 m-auto"
              onClick={() => removeToast(toast.id)}
              aria-label="Close"
            ></button>
          </div>
        </div>
      ))}
    </div>
  )
  const handleMarkInactive = async (device) => {

    if (!window.confirm("Mark this device as Inactive?")) {
      return
    }

    try {

      const result = await markSubscriptionInactive({
        subscription_id: device.id,
        updated_by: sessionStorage.getItem("userId")
      })

      if (result.status) {
        showToast("Device marked as inactive", "success")
        fetchReports()
      } else {
        showToast(result.message, "error")
      }

    } catch (err) {
      console.error(err)
      showToast("Failed to update status", "error")
    }

  }

  const handleMarkActive = async (device) => {

    if (!window.confirm("Mark this device as Active (Renew AMC)?")) {
      return
    }

    try {

      const result = await markSubscriptionActive({
        subscription_id: device.id,
        updated_by: sessionStorage.getItem("userId")
      })

      if (result.status) {
        showToast("Device marked as active", "success")
        fetchReports()
      } else {
        showToast(result.message, "error")
      }

    } catch (err) {
      console.error(err)
      showToast("Failed to update status", "error")
    }

  }
  const bulkMarkActive = async (client) => {

    if (!window.confirm("Activate all inactive devices for this client?")) return

    try {

      const promises = []

      client.areas.forEach(area => {

        area.devices.forEach(device => {

          if (device.isInactive) {

            promises.push(
              markSubscriptionActive({
                subscription_id: device.id,
                updated_by: sessionStorage.getItem("userId")
              })
            )

          }

        })

      })

      await Promise.all(promises)

      showToast("All devices activated successfully", "success")

      fetchReports()

    } catch (err) {

      console.error(err)
      showToast("Bulk activation failed", "error")

    }

  }

  const bulkMarkInactive = async (client) => {

    if (!window.confirm("Mark all expired devices as inactive?")) return

    try {

      const promises = []

      client.areas.forEach(area => {

        area.devices.forEach(device => {

          const days = getDaysRemaining(device.planEnd)

          if (days.text === "Expired") {

            promises.push(
              markSubscriptionInactive({
                subscription_id: device.id,
                updated_by: sessionStorage.getItem("userId")
              })
            )

          }

        })

      })

      await Promise.all(promises)

      showToast("Expired devices marked inactive", "success")

      fetchReports()

    } catch (err) {

      console.error(err)
      showToast("Bulk update failed", "error")

    }

  }
  const getToastIcon = (type) => {
    switch (type) {
      case "success":
        return "check-circle"
      case "error":
        return "exclamation-triangle"
      case "warning":
        return "exclamation-circle"
      case "info":
        return "info-circle"
      default:
        return "info-circle"
    }
  }

  const getToastTitle = (type) => {
    switch (type) {
      case "success":
        return "Success!"
      case "error":
        return "Error!"
      case "warning":
        return "Warning!"
      case "info":
        return "Information"
      default:
        return "Notification"
    }
  }

  // Fetch data on component mount
  useEffect(() => {

    const id = sessionStorage.getItem("clientId")

    if (id) {
      setClientId(id)
    }

  }, [])

  useEffect(() => {

    if (!clientId) return

    fetchReports()

  }, [clientId])

  // Check for expiring subscriptions
  useEffect(() => {
    checkExpiringSubscriptions()
  }, [allData])

  const fetchReports = async () => {
    try {
      setLoading(true)
      setError(null)
      showToast("Fetching Subscription reports...", "info", 2000)

      const [amcResponse, expiredResponse, inactiveResponse] = await Promise.all([getAMCReport(), getExpiredAMCReport(), getInactiveAMCReport()])

      let combinedData = []
      console.log("clientId:", sessionStorage.getItem("clientId"))
      if (amcResponse?.data?.length) {
        console.log("AMC records:", amcResponse.data.length)
      }

      if (expiredResponse?.data?.length) {
        console.log("Expired records:", expiredResponse.data.length)
      }

      if (inactiveResponse?.data?.length) {
        console.log("Inactive records:", inactiveResponse.data.length)
      }

      // Process AMC (Active) data - mapping based on stored procedure structure
      if (amcResponse?.status && Array.isArray(amcResponse.data) && amcResponse.data.length > 0) {
        const activeData = amcResponse.data.map((item) => ({
          id: item.id,
          planStart: item.planStart,
          planEnd: item.planEnd,
          UserID: item.UserID || item.userID,
          deviceID: item.deviceID,
          createdAt: item.createdAt,
          updateAt: item.updateAt,
          createdBy: item.createdBy,
          updatedBy: item.updatedBy,
          planId: item.planId,
          status: item.status,
          productId: item.productId,
          DocRefNumber: item.DocRefNumber,

          ProductName: item.ProductName || "N/A",
          SubscriberName: item.SubscriberName || "N/A",
          ContactNumber: item.ContactNumber || "N/A",
          area_code: item.area_code || "N/A",
          CreatedBy: item.CreatedBy || "N/A",
          UpdatedBy: item.UpdatedBy || "N/A",
          rms_balance: item.rms_balance || "N/A",
          contract_type: item.contract_type || "N/A",

          statusClass: item.status === "INA" ? "secondary" : "success",

          isExpired: item.status !== "INA" && new Date(item.planEnd) < new Date(),

          isInactive: false,   // ✅ ADD THIS LINE

          isExpiringThisMonth: item.status !== "INA" && isExpiringThisMonth(item.planEnd),
        }))

        combinedData = [...combinedData, ...activeData]
      }

      // Process Expired AMC data
      if (expiredResponse.status && expiredResponse.data) {
        const expiredData = expiredResponse.data.map((item) => ({
          // Subscription fields from stored procedure
          id: item.id,
          planStart: item.planStart,
          planEnd: item.planEnd,
          UserID: item.UserID || item.userID,
          deviceID: item.deviceID,
          createdAt: item.createdAt,
          updateAt: item.updateAt,
          createdBy: item.createdBy,
          updatedBy: item.updatedBy,
          planId: item.planId,
          status: item.status,
          productId: item.productId,
          DocRefNumber: item.DocRefNumber,

          // Joined fields from stored procedure
          ProductName: item.ProductName || "N/A",
          SubscriberName: item.SubscriberName || "N/A",
          ContactNumber: item.ContactNumber || "N/A",
          area_code: item.area_code || "N/A",
          CreatedBy: item.CreatedBy || "N/A",
          UpdatedBy: item.UpdatedBy || "N/A",
          rms_balance: item.rms_balance || "N/A",
          contract_type: item.contract_type || "N/A",
          // Computed fields
          statusClass: item.status === "INA" ? "secondary" : "danger",
          isExpired: item.status !== "INA",
          isInactive: false,
          isExpiringThisMonth: false,
        }))

        combinedData = [...combinedData, ...expiredData]
      }
      // Process Inactive AMC data
      if (inactiveResponse?.status && Array.isArray(inactiveResponse.data) && inactiveResponse.data.length > 0) {

        const inactiveData = inactiveResponse.data.map((item) => ({

          id: item.id,
          planStart: item.planStart,
          planEnd: item.planEnd,
          UserID: item.UserID || item.userID,
          deviceID: item.deviceID,
          createdAt: item.createdAt,
          updateAt: item.updateAt,
          createdBy: item.createdBy,
          updatedBy: item.updatedBy,
          planId: item.planId,
          status: item.status,
          productId: item.productId,
          DocRefNumber: item.DocRefNumber,

          ProductName: item.ProductName || "N/A",
          SubscriberName: item.SubscriberName || "N/A",
          ContactNumber: item.ContactNumber || "N/A",
          area_code: item.area_code || "N/A",
          CreatedBy: item.CreatedBy || "N/A",
          UpdatedBy: item.UpdatedBy || "N/A",
          rms_balance: item.rms_balance || "N/A",
          contract_type: item.contract_type || "N/A",

          statusClass: "secondary",
          isExpired: false,
          isInactive: true,
          isExpiringThisMonth: false

        }))

        combinedData = [...combinedData, ...inactiveData]

      }
      console.log("Final combined records:", combinedData.length)

      setAllData([...combinedData])

      const inactiveCount = combinedData.filter((item) => item.isInactive).length
      // Success toast with statistics
      const activeCount = combinedData.filter(
        (item) => !item.isExpired && !item.isInactive
      ).length
      const expiredCount = combinedData.filter((item) => item.isExpired).length
      const expiringCount = combinedData.filter((item) => item.isExpiringThisMonth).length
      // showToast(
      //   `Successfully loaded ${combinedData.length} records\n• ${activeCount} active, ${expiringCount} expiring, ${expiredCount} expired`,
      //   "success",
      //   5000,
      // )
    } catch (err) {
      console.error("Error fetching reports:", err)
      const errorMessage = err.response?.data?.message || err.message || "Failed to fetch reports"
      setError(errorMessage)
      showToast(
        `Failed to fetch Subscriptions reports: ${errorMessage}. Please check your connection and try again.`,
        "error",
        6000,
      )
    } finally {
      setLoading(false)
    }
  }

  const isExpiringThisMonth = (planEndDate) => {
    if (!planEndDate) return false
    const endDate = new Date(planEndDate)
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    return endDate.getMonth() === currentMonth && endDate.getFullYear() === currentYear && endDate >= now
  }
  const isExpiringInDays = (planEndDate, days) => {
    if (!planEndDate) return false

    const end = new Date(planEndDate)
    const now = new Date()

    const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24))

    return diffDays >= 0 && diffDays <= days
  }
  const checkExpiringSubscriptions = () => {
    const groupedExpiring = groupExpiringByClient(allData)
    setExpiringThisMonth(groupedExpiring)

    // if (groupedExpiring.length > 0) {
    //   showToast(
    //     `⚠️ ${groupedExpiring.length} client${groupedExpiring.length > 1 ? "s" : ""} have subscriptions expiring this month! Please review and take action.`,
    //     "warning",
    //     8000,
    //   )
    // }
  }
  const handleFilterChange = (filterType, value) => {

    setFilters((prev) => {

      let newFilters = {
        ...prev,
        [filterType]: value
      }

      // if contract filter used → reset status
      if (filterType === "contractType") {
        newFilters.status = "all"
      }

      return newFilters

    })

  }
  const handleCardFilter = (type) => {

    setFilters((prev) => ({
      ...prev,
      status: type,
      contractType: "all"   // reset contract filter
    }))

    setCurrentPage(1)

  }

  const clearFilters = () => {
    setFilters({
      status: "all",
      searchTerm: "",
      dateRange: "all",
      customStartDate: "",
      customEndDate: "",
      product: "all",
      contractType: "all",

    })
    showToast("All filters cleared. Showing all records.", "info", 2000)
  }

  const handleSort = (key) => {

    let direction = "asc"

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }

    setSortConfig({ key, direction })

    showToast(`Table sorted by ${key} (${direction}ending)`, "info", 2000)
  }

  const exportToExcel = () => {
    try {
      if (filteredData.length === 0) {
        showToast("No data available to export. Please adjust your filters.", "warning", 4000)
        return
      }

      showToast("Preparing Excel export...", "info", 2000)

      const exportData = filteredData.map((item, index) => ({
        "Serial No": index + 1,
        "Subscription ID": item.id,
        "Client Name": item.SubscriberName || "N/A",
        "Contact Number": item.ContactNumber || "N/A",
        "Area Code": item.area_code || "N/A",
        "Device ID": item.deviceID || "N/A",
        "Product Name": item.ProductName || "N/A",
        "Plan Start Date": formatDate(item.planStart),
        "Plan End Date": formatDate(item.planEnd),
        "Balance": item.rms_balance,
        Status: item.isExpiringThisMonth ? "Expiring This Month" : item.status,
        "Days Remaining": getDaysRemaining(item.planEnd),
        Priority: item.isExpiringThisMonth ? "HIGH - EXPIRING" : item.isExpired ? "EXPIRED" : "ACTIVE",
        "Document Reference": item.DocRefNumber || "N/A",
        "Created By": item.CreatedBy || "N/A",
        "Created At": formatDate(item.createdAt),
      }))

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(exportData)

      const colWidths = [
        { wch: 10 }, // Serial No
        { wch: 15 }, // Subscription ID
        { wch: 25 }, // Client Name
        { wch: 15 }, // Contact Number
        { wch: 12 }, // Area Code
        { wch: 20 }, // Device ID
        { wch: 25 }, // Product Name
        { wch: 15 }, // Plan Start Date
        { wch: 15 }, // Plan End Date
        { wch: 20 }, // Status
        { wch: 15 }, // Days Remaining
        { wch: 20 }, // Priority
        { wch: 20 }, // Document Reference
        { wch: 20 }, // Created By
        { wch: 15 }, // Created At
      ]
      ws["!cols"] = colWidths

      const range = XLSX.utils.decode_range(ws["!ref"])
      ws["!autofilter"] = { ref: ws["!ref"] }

      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "366092" } },
        alignment: { horizontal: "center", vertical: "center" },
      }

      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col })
        if (!ws[cellAddress]) continue
        ws[cellAddress].s = headerStyle
      }

      XLSX.utils.book_append_sheet(wb, ws, "Subscriptions Report")

      const currentDate = new Date().toISOString().split("T")[0]
      const filename = `Subscription_Report_${currentDate}.xlsx`

      XLSX.writeFile(wb, filename)

      showToast(
        `📊 Excel export completed successfully!\n📁 File: ${filename}\n📈 Records: ${filteredData.length} with complete subscription details`,
        "success",
        6000,
      )
    } catch (error) {
      console.error("Error exporting to Excel:", error)
      showToast(`Failed to export Excel file: ${error.message}. Please try again.`, "error", 6000)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    })
  }

  const getDaysRemaining = (planEndDate) => {
    if (!planEndDate) return { text: "N/A", color: "secondary" }

    const endDate = new Date(planEndDate)
    const now = new Date()

    const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))

    if (diffDays < 0)
      return { text: "Expired", color: "danger" }

    if (diffDays <= 7)
      return { text: `${diffDays} days`, color: "danger" }

    if (diffDays <= 30)
      return { text: `${diffDays} days`, color: "warning" }

    return { text: `${diffDays} days`, color: "success" }
  }

  const getAreaExpirySummary = (devices) => {

    const now = new Date()

    let expired = 0
    let expiring7 = 0
    let expiring30 = 0

    devices.forEach((device) => {

      const endDate = new Date(device.planEnd)
      const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))

      if (diffDays < 0) expired++
      else if (diffDays <= 7) expiring7++
      else if (diffDays <= 30) expiring30++

    })

    return { expired, expiring7, expiring30 }

  }
  const getClientHealth = (areas) => {

    const now = new Date()

    let expired = 0
    let expiring7 = 0
    let expiring30 = 0
    let active = 0
    let inactive = 0

    areas.forEach(area => {

      area.devices.forEach(device => {

        // NEW CONDITION
        if (device.isInactive) {
          inactive++
          return
        }

        const endDate = new Date(device.planEnd)
        const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))

        if (diffDays < 0) expired++
        else if (diffDays <= 7) expiring7++
        else if (diffDays <= 30) expiring30++
        else active++

      })

    })

    return { expired, expiring7, expiring30, active, inactive }

  }
  const getClientExpirySummary = (areas) => {

    const now = new Date()

    let expired = 0
    let expiring7 = 0
    let expiring30 = 0

    areas.forEach((area) => {

      area.devices.forEach((device) => {

        const endDate = new Date(device.planEnd)
        const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))

        if (diffDays < 0) expired++
        else if (diffDays <= 7) expiring7++
        else if (diffDays <= 30) expiring30++

      })

    })

    return { expired, expiring7, expiring30 }

  }
  const getRowHighlight = (item) => {

    const endDate = new Date(item.planEnd)
    const now = new Date()

    const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))
    if (item.status === "INA") return "table-secondary"
    if (diffDays < 0) return "table-danger"     // expired
    if (diffDays <= 7) return "table-warning"   // urgent
    if (diffDays <= 30) return "table-info"     // upcoming

    return ""
  }
  // Get unique products for filter dropdown
  const uniqueProducts = useMemo(() => {
    return [...new Set(allData.map((item) => item.ProductName).filter(Boolean))]
  }, [allData])
  const filteredData = useMemo(() => {

    let filtered = [...allData]

    // Status filter
    if (filters.status !== "all") {

      switch (filters.status) {

        case "active":
          filtered = filtered.filter(item => !item.isExpired && !item.isInactive)
          break

        case "expired":
          filtered = filtered.filter(item => item.isExpired)
          break

        case "expiring7":
          filtered = filtered.filter(item => isExpiringInDays(item.planEnd, 7))
          break

        case "expiring30":
          filtered = filtered.filter(item => isExpiringInDays(item.planEnd, 30))
          break

        case "nosupport":
          filtered = filtered.filter(item => item.isExpired)
          break

        case "inactive":
          filtered = filtered.filter(item => item.isInactive)
          break
        case "expiringThisMonth":
          filtered = filtered.filter(item => item.isExpiringThisMonth)
          break
      }

    }

    // Search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase()

      filtered = filtered.filter(
        (item) =>
          (item.SubscriberName || "").toLowerCase().includes(searchLower) ||
          (item.ContactNumber || "").toLowerCase().includes(searchLower) ||
          (item.area_code || "").toLowerCase().includes(searchLower) ||
          (item.deviceID || "").toLowerCase().includes(searchLower) ||
          (item.ProductName || "").toLowerCase().includes(searchLower) ||
          (item.DocRefNumber || "").toLowerCase().includes(searchLower)
      )
    }

    // Product filter
    if (filters.product !== "all") {
      filtered = filtered.filter(
        (item) => (item.ProductName || "").toLowerCase() === filters.product.toLowerCase()
      )
    }

    // Contract type filter
    if (filters.contractType !== "all") {
      filtered = filtered.filter(
        (item) => item.contract_type === filters.contractType
      )
    }

    // Date filter
    if (filters.dateRange !== "all") {

      const now = new Date()

      filtered = filtered.filter((item) => {

        const planEnd = new Date(item.planEnd)

        switch (filters.dateRange) {

          case "thisMonth":
            return (
              planEnd.getMonth() === now.getMonth() &&
              planEnd.getFullYear() === now.getFullYear()
            )

          case "nextMonth":
            const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)

            return (
              planEnd.getMonth() === nextMonth.getMonth() &&
              planEnd.getFullYear() === nextMonth.getFullYear()
            )

          case "custom":

            if (filters.customStartDate && filters.customEndDate) {

              const start = new Date(filters.customStartDate)
              const end = new Date(filters.customEndDate)

              return planEnd >= start && planEnd <= end
            }

            return true

          default:
            return true
        }

      })
    }

    // Apply Sorting
    if (sortConfig.key) {

      filtered.sort((a, b) => {

        if (a[sortConfig.key] < b[sortConfig.key])
          return sortConfig.direction === "asc" ? -1 : 1

        if (a[sortConfig.key] > b[sortConfig.key])
          return sortConfig.direction === "asc" ? 1 : -1

        return 0

      })
    }

    return filtered

  }, [allData, filters, sortConfig])

  const groupedClients = useMemo(() => {
    const map = {}
    filteredData.forEach((item) => {
      if (!map[item.UserID]) {
        map[item.UserID] = {
          UserID: item.UserID,
          SubscriberName: item.SubscriberName,
          ContactNumber: item.ContactNumber,
          areas: {},
        }
      }
      if (!map[item.UserID].areas[item.area_code]) {
        map[item.UserID].areas[item.area_code] = {
          area_code: item.area_code,
          devices: []
        }
      }
      map[item.UserID].areas[item.area_code].devices.push(item)
    })
    return Object.values(map).map((client) => ({
      ...client,
      areas: Object.values(client.areas)
    }))
  }, [filteredData])

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = useMemo(() => {
    return groupedClients.slice(indexOfFirstItem, indexOfLastItem)
  }, [groupedClients, indexOfFirstItem, indexOfLastItem])
  const totalPages = Math.ceil(groupedClients.length / itemsPerPage)

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber)
    showToast(`Navigated to page ${pageNumber} of ${totalPages}`, "info", 1500)
  }

  // Check if any filters are active
  const hasActiveFilters =
    filters.status !== "all" || filters.searchTerm !== "" || filters.product !== "all" || filters.dateRange !== "all"
  // ===== Dashboard Global Statistics =====
  const dashboardStats = useMemo(() => {

    const totalDevices = allData.length

    const activeDevices = allData.filter(
      (item) => !item.isExpired && !item.isInactive
    ).length

    const expiredDevices = allData.filter(
      (item) => item.isExpired && !item.isInactive
    ).length

    const inactiveDevices = allData.filter(
      (item) => item.isInactive
    ).length

    const expiringIn7Days = allData.filter(
      (item) => isExpiringInDays(item.planEnd, 7) && !item.isInactive
    ).length

    const expiringIn30Days = allData.filter(
      (item) => isExpiringInDays(item.planEnd, 30) && !item.isInactive
    ).length

    const devicesWithoutSupport = expiredDevices

    return {
      totalDevices,
      activeDevices,
      expiredDevices,
      inactiveDevices,
      expiringIn7Days,
      expiringIn30Days,
      devicesWithoutSupport
    }

  }, [allData])
  if (loading) {
    return (
      <>
        <ToastContainer />
        <div className="container mt-4">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="text-muted">Loading Subscription Reports...</h5>
              <p className="text-muted">Please wait while we fetch your subscription data</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <ToastContainer />
        <div className="container mt-4">
          <div className="alert alert-danger" role="alert">
            <div className="d-flex align-items-center">
              <i className="fas fa-exclamation-triangle fa-2x me-3"></i>
              <div className="flex-grow-1">
                <h5 className="alert-heading">Unable to Load Data</h5>
                <p className="mb-2">{error}</p>
                <small className="text-muted">
                  This could be due to network issues or server problems. Please try refreshing the page.
                </small>
              </div>
            </div>
            <hr />
            <div className="d-flex gap-2">
              <button className="btn btn-outline-danger" onClick={fetchReports}>
                <i className="fas fa-sync-alt me-2"></i>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <ToastContainer />
      <div className="container-fluid mt-4">
        {/* Expiry Notifications */}
        {expiringThisMonth.length > 0 && showNotifications && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-warning alert-dismissible fade show border-0 shadow-sm" role="alert">
                <div className="d-flex align-items-center">
                  <div className="alert-icon me-3">
                    <i className="fas fa-exclamation-triangle fa-2x text-warning"></i>
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="alert-heading mb-2 fw-bold">
                      <i className="fas fa-bell me-2"></i>
                      Subscription Renewal Alert
                    </h5>
                    <p className="mb-2">
                      <strong>{expiringThisMonth.length}</strong> subscription{expiringThisMonth.length > 1 ? "s" : ""}{" "}
                      ending this month require immediate attention.
                    </p>
                    <div className="row g-2">
                      {expiringThisMonth.slice(0, 3).map((client, index) => (
                        <div key={index} className="col-md-6">
                          <div className="card border-warning bg-warning bg-opacity-10 h-100">
                            <div className="card-body py-2 px-3">
                              <div className="d-flex align-items-start">
                                <i className="fas fa-user-clock me-2 text-warning mt-1"></i>
                                <div>
                                  <div className="fw-semibold small">
                                    {client.SubscriberName}
                                  </div>
                                  <div className="text-muted small">
                                    {(client.devices || []).length} Devices • Renewal Date: {formatDate(client.nearestExpiry)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {expiringThisMonth.length > 3 && (
                      <small className="text-muted mt-2 d-block">
                        <i className="fas fa-info-circle me-1"></i>
                        ...and {expiringThisMonth.length - 3} more. Use "Expiring This Month" filter to view all.
                      </small>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowNotifications(false)
                    showToast("Expiry alert dismissed. You can still filter for expiring subscriptions.", "info", 3000)
                  }}
                  aria-label="Close"
                ></button>
              </div>
            </div>
          </div>
        )}

        {/* Professional Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2">
              {/* Title Section */}
              <div className="d-flex align-items-center">
                <div className="rounded-3 p-3 me-3" style={{ backgroundColor: "#3b82f6", color: "white" }}>
                  <i className="fas fa-calendar-check fa-2x"></i>
                </div>
                <div>
                  <h2 className="h2 mb-1 fw-bold" style={{ color: "#1e293b" }}>
                    Subscription Management
                  </h2>
                  <p className="mb-0 small" style={{ color: "#64748b" }}>
                    <i className="fas fa-info-circle me-1"></i>
                    Manage and monitor all subscription records with advanced filtering and analytics
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-1 flex-wrap">
                <button
                  className="btn d-flex align-items-center text-white"
                  style={{ backgroundColor: "#059669", borderColor: "#059669" }}
                  onClick={exportToExcel}
                  disabled={filteredData.length === 0}
                >
                  <i className="fas fa-file-excel me-2"></i>
                  Export Data
                  {filteredData.length > 0 && (
                    <span className="badge bg-white ms-2 fw-bold" style={{ color: "#059669" }}>
                      {filteredData.length}
                    </span>
                  )}
                </button>

                <button
                  className="btn d-flex align-items-center"
                  style={{ backgroundColor: "#f8fafc", borderColor: "#e2e8f0", color: "#475569" }}
                  onClick={fetchReports}
                >
                  <i className="fas fa-sync-alt me-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Summary Cards with Better Color Scheme */}
        {/* Advanced Dashboard Cards */}

        <div className="kpi-bar d-flex flex-wrap align-items-center gap-2 mb-3">

          {/* Total always visible */}
          <div
            className={`kpi-chip total ${filters.status === "all" ? "active-chip" : ""}`}
            onClick={() => handleCardFilter("all")}
          >
            📦 Total Devices <strong>{dashboardStats.totalDevices}</strong>
          </div>

          {/* Active */}
          {dashboardStats.activeDevices > 0 && (
            <div
              className={`kpi-chip active ${filters.status === "active" ? "active-chip" : ""}`}
              onClick={() => handleCardFilter("active")}
            >
              🟢 Under AMC <strong>{dashboardStats.activeDevices}</strong>
            </div>
          )}

          {/* Expired */}
          {dashboardStats.expiredDevices > 0 && (
            <div
              className={`kpi-chip expired ${filters.status === "expired" ? "active-chip" : ""}`}
              onClick={() => handleCardFilter("expired")}
            >
              🔴 Need Renewal <strong>{dashboardStats.expiredDevices}</strong>
            </div>
          )}

          {/* Inactive */}
          {dashboardStats.inactiveDevices > 0 && (
            <div
              className={`kpi-chip inactive ${filters.status === "inactive" ? "active-chip" : ""}`}
              onClick={() => handleCardFilter("inactive")}
            >
              ⚫ Discontinued <strong>{dashboardStats.inactiveDevices}</strong>
            </div>
          )}

          {/* Expiring 7 Days */}
          {dashboardStats.expiringIn7Days > 0 && (
            <div
              className={`kpi-chip warning ${filters.status === "expiring7" ? "active-chip" : ""}`}
              onClick={() => handleCardFilter("expiring7")}
            >
              ⏳ Renewal In 7d <strong>{dashboardStats.expiringIn7Days}</strong>
            </div>
          )}

          {/* Expiring 30 Days */}
          {dashboardStats.expiringIn30Days > 0 && (
            <div
              className={`kpi-chip info ${filters.status === "expiring30" ? "active-chip" : ""}`}
              onClick={() => handleCardFilter("expiring30")}
            >
              📅 Renewal In 30d <strong>{dashboardStats.expiringIn30Days}</strong>
            </div>
          )}

          {/* No Support */}
          {/* {dashboardStats.devicesWithoutSupport > 0 && (
            <div
              className={`kpi-chip support ${filters.status === "nosupport" ? "active-chip" : ""}`}
              onClick={() => handleCardFilter("nosupport")}
            >
              🛠 Out of Support <strong>{dashboardStats.devicesWithoutSupport}</strong>
            </div>
          )} */}

          <div className="vr mx-2"></div>

          <span className="fw-semibold small">Contract:</span>

          <button
            className={`chip-filter ${filters.contractType === "all" ? "active" : ""}`}
            onClick={() => handleFilterChange("contractType", "all")}
          >
            All
          </button>

          <button
            className={`chip-filter ${filters.contractType === "AMC" ? "active" : ""}`}
            onClick={() => handleFilterChange("contractType", "AMC")}
          >
            AMC
          </button>

          <button
            className={`chip-filter ${filters.contractType === "WRT" ? "active" : ""}`}
            onClick={() => handleFilterChange("contractType", "WRT")}
          >
            Warranty
          </button>

        </div>
        {/* Product Filter */}

        {uniqueProducts.length > 1 && (
          <>
            <div className="vr mx-2"></div>

            <span className="fw-semibold small">Product:</span>

            <button
              className={`chip-filter ${filters.product === "all" ? "active" : ""}`}
              onClick={() => handleFilterChange("product", "all")}
            >
              All
            </button>

            {uniqueProducts.map((product) => (
              <button
                key={product}
                className={`chip-filter ${filters.product === product ? "active" : ""}`}
                onClick={() => handleFilterChange("product", product)}
              >
                {product}
              </button>
            ))}
          </>
        )}
        {/* Main Data Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-4">
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
                  <div>
                    <h5 className="mb-1 fw-bold" style={{ color: "#1e293b" }}>
                      <i className="fas fa-table me-2" style={{ color: "#3b82f6" }}></i>
                      Subscription Records ({groupedClients.length} clients) with ({filteredData.length} records)
                    </h5>
                    <p className="mb-0 small" style={{ color: "#64748b" }}>
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, groupedClients.length)} entries
                      {hasActiveFilters && (
                        <span className="ms-2">
                          <i className="fas fa-filter" style={{ color: "#3b82f6" }}></i>
                          <span style={{ color: "#3b82f6" }}>Filtered</span>
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th></th>
                        <th>Client</th>
                        <th>Contact</th>
                        <th>Areas</th>
                        <th>Devices</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>

                      {currentItems.map((client, clientIndex) => {

                        const totalDevices = (client.areas || []).reduce(
                          (sum, area) => sum + (area.devices || []).length,
                          0
                        )

                        const health = getClientHealth(client.areas || [])
                        const hasInactive = client.areas.some(area =>
                          area.devices.some(device => device.isInactive)
                        )

                        const hasExpired = (client.areas || []).some(area =>
                          (area.devices || []).some(device => {

                            if (device.isInactive) return false

                            const days = getDaysRemaining(device.planEnd)

                            return days.text === "Expired"

                          })
                        )

                        const allActive = !hasInactive && !hasExpired
                        return (

                          <>

                            {/* CLIENT SUMMARY ROW */}

                            <tr
                              key={client.UserID}
                              style={{ background: "#f8fafc", cursor: "pointer" }}
                              onClick={() => toggleClient(client.UserID)}
                            >

                              <td>{expandedClients[client.UserID] ? "▼" : "▶"}</td>

                              <td>
                                <strong>{client.SubscriberName}</strong>
                              </td>

                              <td>{client.ContactNumber}</td>

                              <td>
                                {(client.areas || []).map((a, i) => (
                                  <span key={i} className="badge bg-secondary me-1">
                                    {a.area_code}
                                  </span>
                                ))}
                              </td>

                              <td>
                                <span className="badge bg-primary">
                                  {totalDevices}
                                </span>
                              </td>

                              <td>

                                {health.inactive === totalDevices && (
                                  <span className="badge bg-dark">
                                    ⚫ Inactive
                                  </span>
                                )}

                                {health.expired > 0 && (
                                  <span className="badge bg-danger">
                                    🔴 {health.expired} Expired
                                  </span>
                                )}

                                {health.active === totalDevices && (
                                  <span className="badge bg-success">
                                    {client.areas[0]?.devices[0]?.contract_type === "WRT"
                                      ? "🟢 Warranty"
                                      : `🟢 ${health.active} Active`}
                                  </span>
                                )}

                              </td>

                              <td>

                                {hasExpired && (
                                  <button
                                    className="btn btn-sm btn-danger me-2"
                                    onClick={() => bulkMarkInactive(client)}
                                  >
                                    <i className="fas fa-ban me-1"></i>
                                    Inactivate All
                                  </button>
                                )}

                                {!hasExpired && hasInactive && (
                                  <button
                                    className="btn btn-sm btn-success me-2"
                                    onClick={() => bulkMarkActive(client)}
                                  >
                                    <i className="fas fa-check me-1"></i>
                                    Activate All
                                  </button>
                                )}

                                <button
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <i className="fas fa-eye me-1"></i>
                                  View
                                </button>

                              </td>

                            </tr>


                            {/* EXPANDED DEVICE TABLE */}

                            {expandedClients[client.UserID] && (

                              <tr>

                                <td colSpan="7">

                                  <table className="table table-sm table-bordered mt-2">

                                    <thead className="table-light">

                                      <tr>
                                        <th>Device</th>
                                        <th>Area</th>
                                        <th>Product</th>
                                        <th>Start</th>
                                        <th>End</th>
                                        <th>RMS</th>
                                        <th>Status</th>
                                      </tr>

                                    </thead>

                                    <tbody>

                                      {(client.areas || []).map(area =>

                                        (area.devices || []).map(device => (

                                          <tr key={device.id}>

                                            <td>{device.deviceID}</td>

                                            <td>
                                              <span className="badge bg-dark">
                                                {area.area_code}
                                              </span>
                                            </td>

                                            <td>{device.ProductName}</td>

                                            <td>{formatDate(device.planStart)}</td>

                                            <td>{formatDate(device.planEnd)}</td>

                                            <td>{device.rms_balance}</td>

                                            <td>

                                              {(() => {
                                                const days = getDaysRemaining(device.planEnd)

                                                if (device.isInactive) {
                                                  return <span className="badge bg-secondary">Inactive</span>
                                                }

                                                if (days.text === "Expired") {
                                                  return <span className="badge bg-danger">Expired</span>
                                                }

                                                return <span className="badge bg-success">Active</span>
                                              })()}

                                            </td>

                                            <td>
                                              {(() => {

                                                const days = getDaysRemaining(device.planEnd)

                                                // CASE 1 — already inactive
                                                if (device.isInactive) {
                                                  return (
                                                    <button
                                                      className="btn btn-sm btn-outline-success"
                                                      onClick={() => handleMarkActive(device)}
                                                    >
                                                      Mark Active
                                                    </button>
                                                  )
                                                }

                                                // CASE 2 — expired but still active
                                                if (days.text === "Expired") {
                                                  return (
                                                    <button
                                                      className="btn btn-sm btn-outline-danger"
                                                      onClick={() => handleMarkInactive(device)}
                                                    >
                                                      Mark Inactive
                                                    </button>
                                                  )
                                                }

                                                return null

                                              })()}
                                            </td>

                                          </tr>

                                        ))

                                      )}

                                    </tbody>

                                  </table>

                                </td>

                              </tr>

                            )}

                          </>

                        )

                      })}

                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="card-footer">
                  <nav aria-label="Page navigation">
                    <ul className="pagination justify-content-center mb-0">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? "active" : ""}`}>
                              <button className="page-link" onClick={() => paginate(pageNumber)}>
                                {pageNumber}
                              </button>
                            </li>
                          )
                        } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                          return (
                            <li key={pageNumber} className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          )
                        }
                        return null
                      })}
                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SubscriptionManagement